import { useQuery } from '@apollo/client';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { AppState } from '../../lib/store';
import { RESCUE_PORTAL_BETA_TESTER_FLAG, currentUserBetaTesterFlagsQuery } from '../../lib/utils';
import { gqlTypes } from '../../types/gqlTypes';

/**
 * Renders `children` unless the current user has the `rescuePortal:betaTester:enabled`
 * targeted feature flag, in which case they are redirected to `to` (typically a
 * matching page in the new rescue portal). Used to gradually migrate beta cohort
 * traffic off the legacy chip-management flow.
 *
 * The query uses `errorPolicy: 'all'` so a backend hiccup or an unconfigured flag
 * never blocks the underlying page from rendering — non-redirected users see the
 * legacy page as before.
 */
export default function RedirectRescuePortalBetaTester({ to, children }: { to: string; children: ReactNode }) {
  const isLoggedIn = useSelector((state: AppState) => !!state.session);
  const { data, loading } = useQuery<gqlTypes.currentUserBetaTesterFlags>(currentUserBetaTesterFlagsQuery, {
    skip: !isLoggedIn,
    errorPolicy: 'all',
  });

  if (isLoggedIn && loading) {
    return null;
  }

  const isRescuePortalBetaTester = !!data?.currentUser.targetedFeatureFlags?.some(
    (flag) => flag.name === RESCUE_PORTAL_BETA_TESTER_FLAG && flag.value,
  );
  if (isRescuePortalBetaTester) {
    return <Redirect to={to} />;
  }

  return <>{children}</>;
}
